import {
  DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER,
  getWorkItemStatusCategoryLabel,
  normalizeWorkItemStatusCategoryKey,
  workItemStatusCategorySortKey,
} from "../constants/workItemStatusCategoryTheme";

/**
 * Maps GET /dashboard `taskStatistics` / `bugStatistics` to Chart.js.
 *
 * **Primary (stacked):** `byColumnAndPriority[]` — each row:
 * `{ column, priority, count }` where `column` is a workflow category (see `normalizeDashboardColumnKey`).
 *
 * **Synthesis (stacked):** when joint rows are absent but `byPriority[]` exists together with
 * `byCategory` (or `byStatus`), counts are split **proportionally** per priority so column
 * totals match `byCategory` and priority totals match `byPriority` (integer split, largest remainder).
 *
 * **Fallback:** `byCategory` / `byStatus` only — simple single-series bars (no priority breakdown).
 */

const TASK_BAR_COLORS = ["#0a91d5", "#2563eb", "#09b570", "#f97316", "#9333ea", "#64748b"];
const BUG_BAR_COLORS = ["#04a4a8", "#0891b2", "#22c55e", "#f97316", "#e7683a", "#9333ea"];

/** X-axis: all six workflow categories (same order as filters / Kanban). */
export const WORKFLOW_CATEGORY_KEYS = [...DEFAULT_WORK_ITEM_STATUS_CATEGORY_ORDER];
export const WORKFLOW_CATEGORY_LABELS = WORKFLOW_CATEGORY_KEYS.map((key) =>
  getWorkItemStatusCategoryLabel(key)
);

function createEmptyCategoryMatrix() {
  /** @type {Record<string, Record<string, number>>} */
  const matrix = {};
  WORKFLOW_CATEGORY_KEYS.forEach((key) => {
    matrix[key] = {};
  });
  return matrix;
}

const PRIORITY_SORT_ORDER = ["Highest", "Urgent", "High", "Medium", "Low", "Lowest", "Unspecified"];

const TASK_PRIORITY_COLORS = [
  "#dc2626",
  "#b91c1c",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#2563eb",
  "#9333ea",
  "#64748b",
];

const BUG_PRIORITY_COLORS = [
  "#0e7490",
  "#be123c",
  "#ea580c",
  "#ca8a04",
  "#16a34a",
  "#1d4ed8",
  "#7c3aed",
  "#52525b",
];

/**
 * Map API column / category to a canonical workflow key (NEW, ESTIMATIONS, …, DONE).
 * Accepts legacy OPEN → NEW and CLOSED → DONE via theme aliases.
 */
export function normalizeDashboardColumnKey(raw) {
  const key = workItemStatusCategorySortKey(raw);
  if (!key || !WORKFLOW_CATEGORY_KEYS.includes(key)) return null;
  return key;
}

function sortPriorityLabels(keys) {
  return [...keys].sort((a, b) => {
    const sa = String(a);
    const sb = String(b);
    const ia = PRIORITY_SORT_ORDER.findIndex((p) => p.toLowerCase() === sa.toLowerCase());
    const ib = PRIORITY_SORT_ORDER.findIndex((p) => p.toLowerCase() === sb.toLowerCase());
    if (ia === -1 && ib === -1) return sa.localeCompare(sb);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

/**
 * Legend + stack series order: always include Highest → Urgent → High → … → Unspecified,
 * then any extra priority names from the API that are not in that list.
 * @param {string[]} presentKeys priorities seen in the data
 */
function buildFullPriorityOrder(presentKeys) {
  const extras = sortPriorityLabels(
    presentKeys.filter(
      (k) => !PRIORITY_SORT_ORDER.some((o) => o.toLowerCase() === String(k).toLowerCase())
    )
  );
  return [...PRIORITY_SORT_ORDER, ...extras];
}

/** Human-readable workflow category (matches Kanban badges / filters). */
export function formatWorkflowCategoryLabel(category) {
  const raw = String(category ?? "").trim();
  if (!raw) return "—";
  const label = getWorkItemStatusCategoryLabel(raw);
  if (label !== "Status") return label;
  return raw
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/**
 * @param {Array<{ category?: string, count?: number }>} [byCategory]
 * @param {number|null|undefined} [total]
 */
export function buildCategorySummaryParts(byCategory, total) {
  if (!Array.isArray(byCategory) || byCategory.length === 0) {
    return { parts: [], total: null };
  }
  const parts = byCategory.map((row) => ({
    label: formatWorkflowCategoryLabel(row?.category),
    count: Number(row?.count) || 0,
  }));
  const totalNum = total != null && !Number.isNaN(Number(total)) ? Number(total) : null;
  return { parts, total: totalNum };
}

/** @deprecated Prefer {@link buildCategorySummaryParts} + {@link DashboardCategorySummary} for layout. */
export function formatCategorySummaryText(byCategory, total) {
  const { parts, total: totalCount } = buildCategorySummaryParts(byCategory, total);
  if (!parts.length) return "";
  const line = parts.map((p) => `${p.label}: ${p.count}`).join(" · ");
  const suffix = totalCount != null ? ` · Total: ${totalCount}` : "";
  return `${line}${suffix}`;
}

/**
 * Build x-axis labels for byStatus rows (legacy helper).
 */
export function buildStatusBarLabels(rows) {
  if (!Array.isArray(rows)) return [];
  const nameOccurrences = {};
  rows.forEach((r) => {
    const key = String(r?.statusName ?? "").trim();
    nameOccurrences[key] = (nameOccurrences[key] || 0) + 1;
  });
  return rows.map((r) => {
    const base = String(r?.statusName ?? r?.category ?? "Status").trim() || "Status";
    const id = r?.statusId != null ? String(r.statusId) : "";
    const key = String(r?.statusName ?? "").trim();
    const dup = nameOccurrences[key] > 1;
    if (dup && id) return `${base} (${id.slice(0, 8)})`;
    return base;
  });
}

function sortCategoryKeys(keys) {
  const rank = (c) => {
    const slot = workItemStatusCategorySortKey(c) || String(c ?? "").toUpperCase();
    const i = WORKFLOW_CATEGORY_KEYS.indexOf(slot);
    return i === -1 ? WORKFLOW_CATEGORY_KEYS.length + (slot.charCodeAt(0) || 0) : i;
  };
  return [...keys].sort((a, b) => rank(a) - rank(b));
}

function aggregateCountsByCategoryFromStatus(byStatusRows) {
  /** @type {Record<string, number>} */
  const agg = {};
  byStatusRows.forEach((r) => {
    const key = workItemStatusCategorySortKey(r?.category);
    if (!key || !WORKFLOW_CATEGORY_KEYS.includes(key)) return;
    agg[key] = (agg[key] || 0) + (Number(r?.count) || 0);
  });
  return agg;
}

/** @returns {Record<string, number>} */
function readCountsByCategory(stats) {
  /** @type {Record<string, number>} */
  const countsByCat = {};
  if (Array.isArray(stats.byCategory) && stats.byCategory.length > 0) {
    stats.byCategory.forEach((row) => {
      const key = workItemStatusCategorySortKey(row?.category);
      if (!key || !WORKFLOW_CATEGORY_KEYS.includes(key)) return;
      countsByCat[key] = (countsByCat[key] || 0) + (Number(row?.count) || 0);
    });
  } else if (Array.isArray(stats.byStatus) && stats.byStatus.length > 0) {
    Object.assign(countsByCat, aggregateCountsByCategoryFromStatus(stats.byStatus));
  }
  return countsByCat;
}

/** Column totals in {@link WORKFLOW_CATEGORY_KEYS} order for stacked-bar synthesis. */
function getWorkflowCategoryColumnTotals(stats) {
  const countsByCat = readCountsByCategory(stats);
  return WORKFLOW_CATEGORY_KEYS.map((key) => countsByCat[key] ?? 0);
}

/**
 * Integer matrix with fixed row and column sums (independence / proportional margins).
 * Cell (i,j) ≈ rowTotals[i] * colTotals[j] / T; uses greedy largest-fraction placement so every
 * value is a whole **task count**, not a decimal share.
 *
 * @param {number[]} rowTotals
 * @param {number[]} colTotals
 * @returns {number[][]}
 */
function independenceIntegerMatrix(rowTotals, colTotals) {
  const R = rowTotals.length;
  const C = colTotals.length;
  const sumR = rowTotals.reduce((a, b) => a + b, 0);
  const sumC = colTotals.reduce((a, b) => a + b, 0);
  if (R === 0 || C === 0 || sumR === 0) return [];

  let col = colTotals.map((c) => Math.max(0, Number(c) || 0));
  let colSum = col.reduce((a, b) => a + b, 0);
  if (colSum === 0) return [];

  if (colSum !== sumR && colSum > 0) {
    const scaled = col.map((c) => (c * sumR) / colSum);
    const floors = scaled.map((x) => Math.floor(x));
    let rem = sumR - floors.reduce((a, b) => a + b, 0);
    const order = [...Array(C).keys()].sort(
      (i, j) => scaled[j] - Math.floor(scaled[j]) - (scaled[i] - Math.floor(scaled[i]))
    );
    col = [...floors];
    for (let k = 0; k < rem; k++) {
      col[order[k % C]] += 1;
    }
  }

  const T = sumR;
  const F = rowTotals.map((r) => col.map((c) => (r * c) / T));
  const I = F.map((row) => row.map((x) => Math.floor(x)));
  const rowRem = rowTotals.map((rt, i) => rt - I[i].reduce((s, v) => s + v, 0));
  const colRem = col.map((ct, j) => ct - I.reduce((s, row) => s + row[j], 0));

  let totalRem = rowRem.reduce((a, b) => a + b, 0);
  let guard = 0;
  while (totalRem > 0 && guard < T * C * 2) {
    guard++;
    let bestI = -1;
    let bestJ = -1;
    let bestScore = -Infinity;
    for (let i = 0; i < R; i++) {
      if (rowRem[i] <= 0) continue;
      for (let j = 0; j < C; j++) {
        if (colRem[j] <= 0) continue;
        const slack = F[i][j] - I[i][j];
        if (slack > bestScore) {
          bestScore = slack;
          bestI = i;
          bestJ = j;
        }
      }
    }
    if (bestI < 0) break;
    I[bestI][bestJ]++;
    rowRem[bestI]--;
    colRem[bestJ]--;
    totalRem--;
  }

  return I;
}

function priorityLabelFromByPriorityRow(row) {
  const name = String(row?.priorityName ?? "").trim();
  if (name) return name;
  const v = String(row?.value ?? "").trim();
  if (!v) return "Unspecified";
  return v.charAt(0) + v.slice(1).toLowerCase();
}

/**
 * Build workflow-category × priority matrix from global `byPriority` + category totals.
 * Uses integer counts only (whole tasks per segment), not fractional shares.
 */
function buildMatrixFromByPriorityAndColumns(stats) {
  const priRows = Array.isArray(stats.byPriority) ? stats.byPriority : [];
  const colTotals = getWorkflowCategoryColumnTotals(stats);
  const matrix = createEmptyCategoryMatrix();
  const priorities = new Set();

  /** @type {Record<string, number>} */
  const priAgg = {};
  priRows.forEach((row) => {
    const label = priorityLabelFromByPriorityRow(row);
    priAgg[label] = (priAgg[label] || 0) + (Number(row.count) || 0);
  });

  const labels = Object.keys(priAgg).filter((l) => priAgg[l] > 0);
  const rowTotals = labels.map((l) => priAgg[l]);
  const grandTotal = rowTotals.reduce((a, b) => a + b, 0);
  const colSum = colTotals.reduce((a, b) => a + b, 0);

  if (grandTotal <= 0 || labels.length === 0 || colSum <= 0) {
    return { matrix, priorities };
  }

  const I = independenceIntegerMatrix(rowTotals, colTotals);
  if (!I.length) {
    return { matrix, priorities };
  }

  labels.forEach((label, i) => {
    priorities.add(label);
    WORKFLOW_CATEGORY_KEYS.forEach((catKey, j) => {
      matrix[catKey][label] = I[i][j] ?? 0;
    });
  });

  return { matrix, priorities };
}

/**
 * Stacked bars: six workflow categories, segments = priority counts.
 * Uses `stats.byColumnAndPriority` (or `byOpenInProgressClosePriority`), else synthesizes from
 * `stats.byPriority` + category totals from `byCategory` / `byStatus`.
 *
 * @param {object|null|undefined} stats
 * @param {{ palette?: "task" | "bug" }} [opts]
 * @returns {{ labels: string[], datasets: Array<{ label: string, data: number[], backgroundColor: string }>, total: number|null, categorySummary: string, stacked: boolean }}
 */
export function mapOpenInProgressClosePriorityStack(stats, opts = {}) {
  const palette = opts.palette === "bug" ? BUG_PRIORITY_COLORS : TASK_PRIORITY_COLORS;
  const empty = {
    labels: WORKFLOW_CATEGORY_LABELS,
    datasets: [],
    total: null,
    categorySummary: "",
    stacked: true,
  };

  if (!stats || typeof stats !== "object") {
    return empty;
  }

  const total = stats.total != null ? Number(stats.total) : null;
  const categorySummary = formatCategorySummaryText(stats.byCategory, total);

  const explicitRows = Array.isArray(stats.byColumnAndPriority)
    ? stats.byColumnAndPriority
    : Array.isArray(stats.byOpenInProgressClosePriority)
      ? stats.byOpenInProgressClosePriority
      : [];

  const matrix = createEmptyCategoryMatrix();
  const priorities = new Set();

  if (explicitRows.length > 0) {
    explicitRows.forEach((row) => {
      const col = normalizeDashboardColumnKey(
        row.column ?? row.phase ?? row.statusColumn ?? row.bucket ?? row.category
      );
      const pr = String(row.priority ?? row.priorityName ?? "Unspecified").trim() || "Unspecified";
      if (!col) return;
      const n = Number(row.count) || 0;
      matrix[col][pr] = (matrix[col][pr] || 0) + n;
      priorities.add(pr);
    });
  } else {
    const built = buildMatrixFromByPriorityAndColumns(stats);
    Object.assign(matrix, built.matrix);
    built.priorities.forEach((p) => priorities.add(p));
  }

  if (priorities.size === 0) {
    return {
      ...empty,
      total: Number.isFinite(total) ? total : null,
      categorySummary,
    };
  }

  const priorityList = buildFullPriorityOrder(Array.from(priorities));

  const datasets = priorityList.map((pr, idx) => ({
    label: pr,
    data: WORKFLOW_CATEGORY_KEYS.map((ck) => matrix[ck][pr] ?? 0),
    backgroundColor: palette[idx % palette.length],
  }));

  const hasAny = datasets.some((ds) => ds.data.some((v) => Number(v) > 0));
  if (!hasAny) {
    return {
      labels: WORKFLOW_CATEGORY_LABELS,
      datasets: [],
      total: Number.isFinite(total) ? total : null,
      categorySummary,
      stacked: true,
    };
  }

  return {
    labels: WORKFLOW_CATEGORY_LABELS,
    datasets,
    total: Number.isFinite(total) ? total : null,
    categorySummary,
    stacked: true,
  };
}

/**
 * One bar per workflow category (legacy fallback).
 */
export function mapWorkItemStatsToCategoryCountsChart(stats, opts = {}) {
  const colors = opts.palette === "bug" ? BUG_BAR_COLORS : TASK_BAR_COLORS;
  const seriesLabel = opts.palette === "bug" ? "Bugs" : "Tasks";

  if (!stats || typeof stats !== "object") {
    return { labels: [], datasets: [], total: null, categorySummary: "", stacked: false };
  }

  const total = stats.total != null ? Number(stats.total) : null;
  const categorySummary = formatCategorySummaryText(stats.byCategory, total);

  /** @type {Record<string, number>} */
  let countsByCat = {};
  let catKeys = [];

  if (Array.isArray(stats.byCategory) && stats.byCategory.length > 0) {
    const merged = readCountsByCategory(stats);
    catKeys = sortCategoryKeys(Object.keys(merged));
    catKeys.forEach((k) => {
      countsByCat[k] = merged[k] ?? 0;
    });
  } else if (Array.isArray(stats.byStatus) && stats.byStatus.length > 0) {
    countsByCat = aggregateCountsByCategoryFromStatus(stats.byStatus);
    catKeys = sortCategoryKeys(Object.keys(countsByCat));
  }

  if (catKeys.length === 0) {
    return {
      labels: [],
      datasets: [],
      total: Number.isFinite(total) ? total : null,
      categorySummary,
      stacked: false,
    };
  }

  const labels = catKeys.map(formatWorkflowCategoryLabel);
  const data = catKeys.map((c) => countsByCat[c] ?? 0);

  if (!data.some((v) => v > 0)) {
    return {
      labels,
      datasets: [],
      total: Number.isFinite(total) ? total : null,
      categorySummary,
      stacked: false,
    };
  }

  return {
    labels,
    datasets: [
      {
        label: seriesLabel,
        data,
        backgroundColor: catKeys.map((_, i) => colors[i % colors.length]),
      },
    ],
    total: Number.isFinite(total) ? total : null,
    categorySummary,
    stacked: false,
  };
}

/**
 * Prefer six-category **stacked by priority**; else simple category bars.
 */
export function mapDashboardWorkItemStatsChart(stats, opts = {}) {
  const stacked = mapOpenInProgressClosePriorityStack(stats, opts);
  const chart =
    stacked.datasets.length > 0 ? stacked : mapWorkItemStatsToCategoryCountsChart(stats, opts);
  const categorySummaryParts = buildCategorySummaryParts(stats?.byCategory, chart.total);
  return { ...chart, categorySummaryParts };
}

/**
 * @deprecated
 */
export function mapWorkItemStatsToStackedBarChart(stats, opts = {}) {
  return mapDashboardWorkItemStatsChart(stats, opts);
}

/**
 * Simple single-series bar by status row (tests / legacy).
 */
export function mapWorkItemStatsToBarChart(stats) {
  if (!stats || typeof stats !== "object") {
    return { labels: [], values: [], total: null, categorySummary: "" };
  }
  const rows = Array.isArray(stats.byStatus) ? stats.byStatus : [];
  const labels = buildStatusBarLabels(rows);
  const values = rows.map((r) => Number(r?.count) || 0);
  const total = stats.total != null ? Number(stats.total) : null;
  const categorySummary = formatCategorySummaryText(stats.byCategory, total);
  return { labels, values, total: Number.isFinite(total) ? total : null, categorySummary };
}
